/**
 * @author
 * David Rivera (jherax)
 * https://github.com/jherax
 */

import isNotObject from './objects/isNotObject';
import root from './root';

/* eslint-disable no-bitwise */

/** @private */
const toString = Object.prototype.toString;
const constructors = [Date, RegExp, Function, String, Number, Boolean];

// Add support for some ES2015 constructors
['Map', 'Set'].forEach((c) => {
  if (c in root) constructors.push(root[c]);
});

/**
 * @private
 * Compares an object against the context.
 *
 * @param {any} obj: the object to compare
 * @returns {Boolean}
 */
function compare(obj) {
  return obj === this;
}

/**
 * @private
 * Creates a deep copy of an object supporting objects with circular references.
 *
 * @param  {any} source: the object to clone
 * @param  {Object} dest: destination object to merge with
 * @param  {Array} cache: the cache for cloned objects
 * @return {any}
 */
function cloner(source, dest, cache) {
  let prop;
  // determines whether @source is a primitive value or a function
  if (isNotObject(source)) return source;
  // checks if @source refers to an object created previously
  if (toString.call(source) === '[object Object]') {
    if (cache.find(compare, source)) return source;
    // keeps references of created objects to prevent circular reference exception
    cache.push(source);
  }
  // determines if the @source is a DOM Node
  if (source.nodeName) return source.cloneNode(true);
  // determines whether @source is an instance of any of the constructors
  if (~constructors.indexOf(source.constructor)) return new source.constructor(source);
  if (source.constructor !== Object && source.constructor !== Array) return source;
  // creates a new object and recursively iterates over its properties
  dest = dest || new source.constructor();
  for (prop in source) {
    // merges @source into @dest
    dest[prop] = cloner(source[prop], dest[prop], cache);
  }
  return dest;
}

/**
 * @public
 * Creates a deep copy of an object supporting objects with circular references.
 *
 * @see https://gist.github.com/jherax/05204bdf9eb47eeffdc8
 *
 * @param  {any} source: the object to clone
 * @return {any}
 */
export default function clone(source) {
  const cache = [];
  // the cache prevents circular reference exception!
  return cloner(source, null, cache);
}

/**
 * @public
 * Merges an object into another supporting objects with circular references.
 * Recursively descends into object properties of source objects, performing a deep copy.
 *
 * @see https://gist.github.com/jherax/05204bdf9eb47eeffdc8
 *
 * @param  {Object} dest: destination object to merge
 * @param  {Object} source: the object to copy from
 * @return {Object}
 */
export function merge(dest, source) {
  if (dest === source) return dest;
  if (isNotObject(dest)) throw Error('First argument must be an object');
  cloner(source, dest, []);
  return dest;
}
